import { ArrowRight, Check } from 'lucide-react';
import { BotaoLink, Etiqueta } from '../../componentes/ui';
import type { CapturaPublica } from '../../api/tipos';
import { numero, reais } from '../../util/formato';
import { CabecalhoSecao } from './CabecalhoSecao';
import { NotasCapturadas } from './Mockups';
import { useCapturaPublica } from './useCapturaPublica';
import base from './publico.module.css';
import css from './Secoes.module.css';

/**
 * Captura automática via certificado A1.
 *
 * O texto vale com a função liberada ou não; o que muda — a etiqueta, o preço
 * e para onde o botão leva — vem de `GET /dfe/publico`. Enquanto a captura
 * estiver em validação, a seção diz "em liberação" e encaminha para o chat:
 * anunciar como pronto o que o cliente ainda não pode contratar seria promessa
 * quebrada.
 */
export function Captura({ logado }: { logado: boolean }) {
  const dados = useCapturaPublica();

  return (
    <section id="captura" className={`${base.secao} ${base.secaoSuave}`}>
      <div className={`${base.conteudo} ${css.captura}`}>
        <div className={css.capturaTexto}>
          <CabecalhoSecao
            sobretitulo="Captura automática · certificado A1"
            titulo="As notas de entrada chegam sozinhas"
            className={css.capturaCabecalho}
          >
            Cadastre o certificado A1 das empresas que o escritório atende. Nós
            consultamos a SEFAZ pelas NF-e emitidas contra cada CNPJ,
            registramos a Ciência da Operação e guardamos o XML completo — sem
            pedir nada ao cliente.
          </CabecalhoSecao>

          <ul className={css.checklist}>
            <li>
              <Check aria-hidden="true" />
              <span>
                Inclusive as notas que o fornecedor não mandou: a fonte é a
                SEFAZ, não a caixa de e-mail do cliente.
              </span>
            </li>
            <li>
              <Check aria-hidden="true" />
              <span>
                Vários CNPJs na mesma conta, cada um com a sua situação, e o ZIP
                do mês pronto para importar.
              </span>
            </li>
            <li>
              <Check aria-hidden="true" />
              <span>
                Cancelamentos chegam como evento e a nota aparece marcada.
              </span>
            </li>
            <li>
              <Check aria-hidden="true" />
              <span>
                Certificado guardado cifrado e usado só para consultar a SEFAZ.
                Avisamos por e-mail antes de ele vencer.
              </span>
            </li>
          </ul>

          <SituacaoCaptura dados={dados} />

          <div className={css.capturaAcoes}>
            <BotaoLink
              to={destino(logado, dados)}
              data-clique="captura-landing"
              aparencia="destaque"
              grande
              iconeDireita={<ArrowRight />}
            >
              {rotulo(logado, dados)}
            </BotaoLink>
          </div>

          <p className={css.capturaNota}>
            NF-e modelo 55. A SEFAZ entrega as notas dos últimos 90 dias —
            quanto antes a empresa entra, menos fica para trás.
          </p>
        </div>

        <div className={css.capturaVisual}>
          <NotasCapturadas />
        </div>
      </div>
    </section>
  );
}

/**
 * Etiqueta, faixas do plano e o aviso de liberação gradual, se ainda for o
 * caso. Também usada no guia da captura. Nada enquanto carrega: o HTML
 * pré-renderizado não afirma nenhum dos dois estados nem preço.
 */
export function SituacaoCaptura({ dados }: { dados: CapturaPublica | null }) {
  if (!dados) return null;
  const ultima = dados.faixas[dados.faixas.length - 1];

  return (
    <div className={css.capturaSituacao}>
      {dados.disponivel ? (
        <Etiqueta tom="ok">Disponível</Etiqueta>
      ) : (
        <Etiqueta tom="info">Em liberação gradual</Etiqueta>
      )}
      <dl className={css.capturaFaixas}>
        {dados.faixas.map((f) => (
          <div key={f.ate}>
            <dt>{f.ate === 1 ? '1 CNPJ' : `Até ${numero(f.ate)} CNPJs`}</dt>
            <dd>
              <b>{reais(f.centavos)}</b>/mês
            </dd>
          </div>
        ))}
        {ultima && dados.adicionalCentavos > 0 && (
          <div>
            <dt>Acima de {numero(ultima.ate)}</dt>
            <dd>
              <b>+{reais(dados.adicionalCentavos)}</b> por CNPJ
            </dd>
          </div>
        )}
      </dl>
      <p className={css.capturaPreco}>
        {dados.disponivel
          ? 'XMLs ilimitados, por conta. Debitado dos seus créditos, sem fidelidade.'
          : 'XMLs ilimitados, por conta. Estamos abrindo para os primeiros escritórios: crie sua conta e peça acesso pelo chat do painel.'}
      </p>
    </div>
  );
}

function destino(logado: boolean, dados: CapturaPublica | null): string {
  if (!logado) return '/criar-conta';
  return dados?.disponivel ? '/painel/empresas' : '/painel/suporte';
}

function rotulo(logado: boolean, dados: CapturaPublica | null): string {
  if (!logado) return 'Criar conta grátis';
  return dados?.disponivel ? 'Cadastrar empresa' : 'Pedir acesso pelo chat';
}
